import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { RadioButton } from "primereact/radiobutton";
import { ProgressBar } from "primereact/progressbar";
import { Dialog } from "primereact/dialog";
import { useFormik } from "formik";
import { useToast } from "../../utils/toast";
import formService from "../../services/forms/form.service";
import { CevreselGostergelerAnketRequestDto } from "../../services/forms/form.dto";
import ProtokolFirmalarComponent from "../../components/ProtokolFirmalarComponent";
import { useAuth } from "../../utils/auth";
import { useNavigate } from "react-router-dom";

const CevreselGostergelerAnket = () => {
  const toast = useToast();
  const auth = useAuth();
  const navigate = useNavigate();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [questionsPerPage] = useState(1); // Her sayfada 1 alt grup (4 soru)

  // Sayfa yüklendiğinde kullanıcının daha önce anket gönderip göndermediğini kontrol et
  useEffect(() => {
    checkExistingSurvey();
  }, []);

  const checkExistingSurvey = async () => {
    try {
      const response = await formService.cevreselGostergelerAnketKontrol();

      if (response.result && !response.result.error) {
        if (response.result.data.has_submitted) {
          setHasSubmitted(true);
        } else {
          setHasSubmitted(false);
        }
      } else {
        console.log("No has_submitted found in response");
      }
    } catch (error: any) {
      console.error("Anket kontrolü sırasında hata:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const submitSurvey = async (values: CevreselGostergelerAnketRequestDto) => {
    // Kullanıcının giriş yapıp yapmadığını kontrol et
    if (!auth.getUser().access_token) {
      toast.show("Anket doldurmak için giriş yapmalısınız.", "error");
      navigate("/loginsebis", { state: { from: window.location.pathname } });
      return;
    }

    try {
      const response = await formService.cevreselGostergelerAnket(values);

      if (response.result && !response.result.error) {
        if (response?.result?.data) {
          toast.show("Çevresel göstergeler anketi başarıyla kaydedildi.", "success");
          formik.resetForm();
          setHasSubmitted(true);
          setShowConfirmDialog(false);
        } else {
          toast.show("Anket kaydedilemedi.", "error");
        }
      } else {
        console.log("Anket kaydedilemedi.");
      }
    } catch (error: any) {
      toast.show("Bir hata oluştu. Lütfen tekrar deneyin.", "error");
    }
  };

  const formik = useFormik<CevreselGostergelerAnketRequestDto>({
    initialValues: {
      // Ekonominin Çevresel ve Kaynak Verimliliği
      // Karbon verimliliği
      karbon_hazirlik: undefined,
      karbon_algı: undefined,
      karbon_yetkinlik: undefined,
      karbon_ilerleme: undefined,

      // Enerji verimliliği
      enerji_hazirlik: undefined,
      enerji_algı: undefined,
      enerji_yetkinlik: undefined,
      enerji_ilerleme: undefined,

      // Malzeme verimliliği
      malzeme_hazirlik: undefined,
      malzeme_algı: undefined,
      malzeme_yetkinlik: undefined,
      malzeme_ilerleme: undefined,

      // Atık yoğunluğu
      atik_hazirlik: undefined,
      atik_algı: undefined,
      atik_yetkinlik: undefined,
      atik_ilerleme: undefined,

      // Geri dönüşüm oranları
      geri_donusum_hazirlik: undefined,
      geri_donusum_algı: undefined,
      geri_donusum_yetkinlik: undefined,
      geri_donusum_ilerleme: undefined,

      // Besin akışları ve dengeleri
      besin_hazirlik: undefined,
      besin_algı: undefined,
      besin_yetkinlik: undefined,
      besin_ilerleme: undefined,

      // Çevreye duyarlı çok faktörlü verimlilik
      verimlilik_hazirlik: undefined,
      verimlilik_algı: undefined,
      verimlilik_yetkinlik: undefined,
      verimlilik_ilerleme: undefined,

      // Doğal Varlık Tabanı
      // Arazi örtüsü ve arazi örtüsü dönüşümü
      arazi_hazirlik: undefined,
      arazi_algı: undefined,
      arazi_yetkinlik: undefined,
      arazi_ilerleme: undefined,

      // Kentsel alanların payı
      kentsel_hazirlik: undefined,
      kentsel_algı: undefined,
      kentsel_yetkinlik: undefined,
      kentsel_ilerleme: undefined,

      // Orman alanı oranı
      orman_hazirlik: undefined,
      orman_algı: undefined,
      orman_yetkinlik: undefined,
      orman_ilerleme: undefined,

      // Orman kaynaklarının kullanım yoğunluğu
      orman_kullanim_hazirlik: undefined,
      orman_kullanim_algı: undefined,
      orman_kullanim_yetkinlik: undefined,
      orman_kullanim_ilerleme: undefined,

      // Sürdürülebilir yönetim sertifikasına sahip ormanlar
      orman_sertifika_hazirlik: undefined,
      orman_sertifika_algı: undefined,
      orman_sertifika_yetkinlik: undefined,
      orman_sertifika_ilerleme: undefined,

      // Tatlı su kaynakları
      su_hazirlik: undefined,
      su_algı: undefined,
      su_yetkinlik: undefined,
      su_ilerleme: undefined,

      // Biyolojik çeşitlilik, ekosistemler ve yaban hayatı
      biyolojik_hazirlik: undefined,
      biyolojik_algı: undefined,
      biyolojik_yetkinlik: undefined,
      biyolojik_ilerleme: undefined,

      // Yaşam Kalitesinin Çevresel Boyutu
      // İyileştirilmiş sanitasyon erişimi
      sanitasyon_hazirlik: undefined,
      sanitasyon_algı: undefined,
      sanitasyon_yetkinlik: undefined,
      sanitasyon_ilerleme: undefined,

      // Nüfusun PM2.5'e maruz kalması
      pm25_hazirlik: undefined,
      pm25_algı: undefined,
      pm25_yetkinlik: undefined,
      pm25_ilerleme: undefined,

      // Hava kirliliği, sağlık riskleri ve maliyetleri
      hava_hazirlik: undefined,
      hava_algı: undefined,
      hava_yetkinlik: undefined,
      hava_ilerleme: undefined,

      // Su temini, sanitasyon ve kanalizasyon arıtımına erişim
      su_temini_hazirlik: undefined,
      su_temini_algı: undefined,
      su_temini_yetkinlik: undefined,
      su_temini_ilerleme: undefined,

      // Ozon maruz kalımı
      ozon_hazirlik: undefined,
      ozon_algı: undefined,
      ozon_yetkinlik: undefined,
      ozon_ilerleme: undefined,

      // Ekonomik Fırsatlar ve Politika Yanıtları
      // Çevreyle ilgili ürünler için pazarlar
      cevre_urunleri_hazirlik: undefined,
      cevre_urunleri_algı: undefined,
      cevre_urunleri_yetkinlik: undefined,
      cevre_urunleri_ilerleme: undefined,

      // Çevreyle ilgili sektörde istihdam ve katma değer
      istihdam_hazirlik: undefined,
      istihdam_algı: undefined,
      istihdam_yetkinlik: undefined,
      istihdam_ilerleme: undefined,

      // Uluslararası finansal akışlar
      finansal_hazirlik: undefined,
      finansal_algı: undefined,
      finansal_yetkinlik: undefined,
      finansal_ilerleme: undefined,

      // Vergiler ve sübvansiyonlar
      vergiler_hazirlik: undefined,
      vergiler_algı: undefined,
      vergiler_yetkinlik: undefined,
      vergiler_ilerleme: undefined,

      // Çevreyle ilgili teknoloji ve inovasyon
      teknoloji_hazirlik: undefined,
      teknoloji_algı: undefined,
      teknoloji_yetkinlik: undefined,
      teknoloji_ilerleme: undefined,

      // Yönetmelikler ve yönetim yaklaşımları
      yonetmelik_hazirlik: undefined,
      yonetmelik_algı: undefined,
      yonetmelik_yetkinlik: undefined,
      yonetmelik_ilerleme: undefined,

      // Eğitim ve beceri geliştirme
      cevre_egitim_hazirlik: undefined,
      cevre_egitim_algı: undefined,
      cevre_egitim_yetkinlik: undefined,
      cevre_egitim_ilerleme: undefined
    },
    validate: (values) => {
      const errors: any = {};

      // Tüm soruların cevaplanıp cevaplanmadığını kontrol et
      const requiredFields = [
        "karbon_hazirlik",
        "karbon_algı",
        "karbon_yetkinlik",
        "karbon_ilerleme",
        "enerji_hazirlik",
        "enerji_algı",
        "enerji_yetkinlik",
        "enerji_ilerleme",
        "malzeme_hazirlik",
        "malzeme_algı",
        "malzeme_yetkinlik",
        "malzeme_ilerleme",
        "atik_hazirlik",
        "atik_algı",
        "atik_yetkinlik",
        "atik_ilerleme",
        "geri_donusum_hazirlik",
        "geri_donusum_algı",
        "geri_donusum_yetkinlik",
        "geri_donusum_ilerleme",
        "besin_hazirlik",
        "besin_algı",
        "besin_yetkinlik",
        "besin_ilerleme",
        "verimlilik_hazirlik",
        "verimlilik_algı",
        "verimlilik_yetkinlik",
        "verimlilik_ilerleme",
        "arazi_hazirlik",
        "arazi_algı",
        "arazi_yetkinlik",
        "arazi_ilerleme",
        "kentsel_hazirlik",
        "kentsel_algı",
        "kentsel_yetkinlik",
        "kentsel_ilerleme",
        "orman_hazirlik",
        "orman_algı",
        "orman_yetkinlik",
        "orman_ilerleme",
        "orman_kullanim_hazirlik",
        "orman_kullanim_algı",
        "orman_kullanim_yetkinlik",
        "orman_kullanim_ilerleme",
        "orman_sertifika_hazirlik",
        "orman_sertifika_algı",
        "orman_sertifika_yetkinlik",
        "orman_sertifika_ilerleme",
        "su_hazirlik",
        "su_algı",
        "su_yetkinlik",
        "su_ilerleme",
        "biyolojik_hazirlik",
        "biyolojik_algı",
        "biyolojik_yetkinlik",
        "biyolojik_ilerleme",
        "sanitasyon_hazirlik",
        "sanitasyon_algı",
        "sanitasyon_yetkinlik",
        "sanitasyon_ilerleme",
        "pm25_hazirlik",
        "pm25_algı",
        "pm25_yetkinlik",
        "pm25_ilerleme",
        "hava_hazirlik",
        "hava_algı",
        "hava_yetkinlik",
        "hava_ilerleme",
        "su_temini_hazirlik",
        "su_temini_algı",
        "su_temini_yetkinlik",
        "su_temini_ilerleme",
        "ozon_hazirlik",
        "ozon_algı",
        "ozon_yetkinlik",
        "ozon_ilerleme",
        "cevre_urunleri_hazirlik",
        "cevre_urunleri_algı",
        "cevre_urunleri_yetkinlik",
        "cevre_urunleri_ilerleme",
        "istihdam_hazirlik",
        "istihdam_algı",
        "istihdam_yetkinlik",
        "istihdam_ilerleme",
        "finansal_hazirlik",
        "finansal_algı",
        "finansal_yetkinlik",
        "finansal_ilerleme",
        "vergiler_hazirlik",
        "vergiler_algı",
        "vergiler_yetkinlik",
        "vergiler_ilerleme",
        "teknoloji_hazirlik",
        "teknoloji_algı",
        "teknoloji_yetkinlik",
        "teknoloji_ilerleme",
        "yonetmelik_hazirlik",
        "yonetmelik_algı",
        "yonetmelik_yetkinlik",
        "yonetmelik_ilerleme",
        "cevre_egitim_hazirlik",
        "cevre_egitim_algı",
        "cevre_egitim_yetkinlik",
        "cevre_egitim_ilerleme"
      ];

      for (const field of requiredFields) {
        if (values[field as keyof CevreselGostergelerAnketRequestDto] === undefined) {
          errors[field] = "Bu soru zorunludur.";
        }
      }

      return errors;
    },
    onSubmit: async (values) => {
      if (hasSubmitted) {
        setShowConfirmDialog(true);
        return;
      }

      await submitSurvey(values);
    }
  });

  // İlerleme hesaplama
  const totalQuestions = 104; // 26 alt kategori x 4 boyut (7+7+5+7 = 26 alt kategori)
  const answeredQuestions = Object.values(formik.values).filter((value) => value !== undefined).length;
  const progressPercentage = Math.round((answeredQuestions / totalQuestions) * 100 * 10) / 10;

  const anketSorulari = [
    {
      alan: "Ekonominin Çevresel ve Kaynak Verimliliği",
      icon: "pi-chart-line",
      color: "green",
      aciklama: "Ekonomik faaliyetlerin çevresel etkilerini ve kaynak kullanım verimliliğini değerlendiren göstergeler",
      altGruplar: [
        {
          baslik: "Karbon Verimliliği",
          icon: "pi-cloud",
          aciklama: "Karbon emisyonları ve karbon ayak izi yönetimi",
          sorular: [
            {
              key: "karbon_hazirlik",
              soru: "Karbon verimliliği konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "karbon_algı",
              soru: "Karbon verimliliği konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "karbon_yetkinlik",
              soru: "Karbon verimliliği konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "karbon_ilerleme",
              soru: "Karbon verimliliği konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        },
        {
          baslik: "Enerji Verimliliği",
          icon: "pi-bolt",
          aciklama: "Enerji tüketimi ve enerji tasarrufu uygulamaları",
          sorular: [
            {
              key: "enerji_hazirlik",
              soru: "Enerji verimliliği konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "enerji_algı",
              soru: "Enerji verimliliği konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "enerji_yetkinlik",
              soru: "Enerji verimliliği konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "enerji_ilerleme",
              soru: "Enerji verimliliği konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        },
        {
          baslik: "Malzeme Verimliliği",
          icon: "pi-box",
          aciklama: "Hammadde kullanımı ve malzeme tasarrufu",
          sorular: [
            {
              key: "malzeme_hazirlik",
              soru: "Malzeme verimliliği konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "malzeme_algı",
              soru: "Malzeme verimliliği konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "malzeme_yetkinlik",
              soru: "Malzeme verimliliği konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "malzeme_ilerleme",
              soru: "Malzeme verimliliği konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        },
        {
          baslik: "Atık Yoğunluğu",
          icon: "pi-trash",
          aciklama: "Atık üretimi ve atık yönetimi uygulamaları",
          sorular: [
            {
              key: "atik_hazirlik",
              soru: "Atık yoğunluğu konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "atik_algı",
              soru: "Atık yoğunluğu konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "atik_yetkinlik",
              soru: "Atık yoğunluğu konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "atik_ilerleme",
              soru: "Atık yoğunluğu konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        },
        {
          baslik: "Geri Dönüşüm Oranları",
          icon: "pi-recycle",
          aciklama: "Geri dönüşüm oranları ve uygulamaları",
          sorular: [
            {
              key: "geri_donusum_hazirlik",
              soru: "Geri dönüşüm oranları konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "geri_donusum_algı",
              soru: "Geri dönüşüm oranları konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "geri_donusum_yetkinlik",
              soru: "Geri dönüşüm oranları konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "geri_donusum_ilerleme",
              soru: "Geri dönüşüm oranları konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        },
        {
          baslik: "Besin Akışları ve Dengeleri",
          icon: "pi-seed",
          aciklama: "Besin döngüleri ve besin yönetimi",
          sorular: [
            {
              key: "besin_hazirlik",
              soru: "Besin akışları ve dengeleri konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "besin_algı",
              soru: "Besin akışları ve dengeleri konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "besin_yetkinlik",
              soru: "Besin akışları ve dengeleri konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "besin_ilerleme",
              soru: "Besin akışları ve dengeleri konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        },
        {
          baslik: "Çevreye Duyarlı Çok Faktörlü Verimlilik",
          icon: "pi-chart-bar",
          aciklama: "Çevresel faktörleri dikkate alan verimlilik ölçümleri",
          sorular: [
            {
              key: "verimlilik_hazirlik",
              soru: "Çevreye duyarlı çok faktörlü verimlilik konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "verimlilik_algı",
              soru: "Çevreye duyarlı çok faktörlü verimlilik konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "verimlilik_yetkinlik",
              soru: "Çevreye duyarlı çok faktörlü verimlilik konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "verimlilik_ilerleme",
              soru: "Çevreye duyarlı çok faktörlü verimlilik konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        }
      ]
    },
    {
      alan: "Doğal Varlık Tabanı",
      icon: "pi-tree",
      color: "blue",
      aciklama: "Doğal kaynakların durumu ve sürdürülebilir kullanımı",
      altGruplar: [
        {
          baslik: "Arazi Örtüsü ve Dönüşümü",
          icon: "pi-map",
          aciklama: "Arazi örtüsü ve arazi kullanım değişiklikleri",
          sorular: [
            {
              key: "arazi_hazirlik",
              soru: "Arazi örtüsü ve arazi örtüsü dönüşümü konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "arazi_algı",
              soru: "Arazi örtüsü ve arazi örtüsü dönüşümü konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "arazi_yetkinlik",
              soru: "Arazi örtüsü ve arazi örtüsü dönüşümü konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "arazi_ilerleme",
              soru: "Arazi örtüsü ve arazi örtüsü dönüşümü konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        },
        {
          baslik: "Kentsel Alanların Payı",
          icon: "pi-building",
          aciklama: "Kentsel alanların toplam arazi içindeki oranı",
          sorular: [
            {
              key: "kentsel_hazirlik",
              soru: "Kentsel alanların payı konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "kentsel_algı",
              soru: "Kentsel alanların payı konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "kentsel_yetkinlik",
              soru: "Kentsel alanların payı konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "kentsel_ilerleme",
              soru: "Kentsel alanların payı konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        },
        {
          baslik: "Orman Alanı Oranı",
          icon: "pi-tree",
          aciklama: "Orman alanları ve orman yönetimi",
          sorular: [
            {
              key: "orman_hazirlik",
              soru: "Orman alanı oranı konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "orman_algı",
              soru: "Orman alanı oranı konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "orman_yetkinlik",
              soru: "Orman alanı oranı konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "orman_ilerleme",
              soru: "Orman alanı oranı konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        },
        {
          baslik: "Orman Kaynaklarının Kullanım Yoğunluğu",
          icon: "pi-chart-pie",
          aciklama: "Orman kaynaklarının sürdürülebilir kullanımı",
          sorular: [
            {
              key: "orman_kullanim_hazirlik",
              soru: "Orman kaynaklarının kullanım yoğunluğu konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "orman_kullanim_algı",
              soru: "Orman kaynaklarının kullanım yoğunluğu konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "orman_kullanim_yetkinlik",
              soru: "Orman kaynaklarının kullanım yoğunluğu konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "orman_kullanim_ilerleme",
              soru: "Orman kaynaklarının kullanım yoğunluğu konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        },
        {
          baslik: "Sürdürülebilir Yönetim Sertifikasına Sahip Ormanlar",
          icon: "pi-check-circle",
          aciklama: "Sertifikalı sürdürülebilir orman yönetimi",
          sorular: [
            {
              key: "orman_sertifika_hazirlik",
              soru: "Sürdürülebilir yönetim sertifikasına sahip ormanlar konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "orman_sertifika_algı",
              soru: "Sürdürülebilir yönetim sertifikasına sahip ormanlar konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "orman_sertifika_yetkinlik",
              soru: "Sürdürülebilir yönetim sertifikasına sahip ormanlar konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "orman_sertifika_ilerleme",
              soru: "Sürdürülebilir yönetim sertifikasına sahip ormanlar konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        },
        {
          baslik: "Tatlı Su Kaynakları",
          icon: "pi-tint",
          aciklama: "Tatlı su kaynakları ve su yönetimi",
          sorular: [
            {
              key: "su_hazirlik",
              soru: "Tatlı su kaynakları konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "su_algı",
              soru: "Tatlı su kaynakları konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "su_yetkinlik",
              soru: "Tatlı su kaynakları konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "su_ilerleme",
              soru: "Tatlı su kaynakları konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        },
        {
          baslik: "Biyolojik Çeşitlilik",
          icon: "pi-leaf",
          aciklama: "Ekosistem çeşitliliği ve yaban hayatı korunması",
          sorular: [
            {
              key: "biyolojik_hazirlik",
              soru: "Biyolojik çeşitlilik, ekosistemler ve yaban hayatı konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "biyolojik_algı",
              soru: "Biyolojik çeşitlilik, ekosistemler ve yaban hayatı konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "biyolojik_yetkinlik",
              soru: "Biyolojik çeşitlilik, ekosistemler ve yaban hayatı konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "biyolojik_ilerleme",
              soru: "Biyolojik çeşitlilik, ekosistemler ve yaban hayatı konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        }
      ]
    },
    {
      alan: "Yaşam Kalitesinin Çevresel Boyutu",
      icon: "pi-heart",
      color: "orange",
      aciklama: "Çevresel faktörlerin insan sağlığı ve yaşam kalitesine etkileri",
      altGruplar: [
        {
          baslik: "İyileştirilmiş Sanitasyon Erişimi",
          icon: "pi-shield",
          aciklama: "Temiz sanitasyon hizmetlerine erişim",
          sorular: [
            {
              key: "sanitasyon_hazirlik",
              soru: "İyileştirilmiş sanitasyon erişimi konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "sanitasyon_algı",
              soru: "İyileştirilmiş sanitasyon erişimi konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "sanitasyon_yetkinlik",
              soru: "İyileştirilmiş sanitasyon erişimi konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "sanitasyon_ilerleme",
              soru: "İyileştirilmiş sanitasyon erişimi konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        },
        {
          baslik: "Nüfusun PM2.5'e Maruz Kalması",
          icon: "pi-cloud",
          aciklama: "Hava kalitesi ve partikül madde maruziyeti",
          sorular: [
            {
              key: "pm25_hazirlik",
              soru: "Nüfusun PM2.5'e maruz kalması konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "pm25_algı",
              soru: "Nüfusun PM2.5'e maruz kalması konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "pm25_yetkinlik",
              soru: "Nüfusun PM2.5'e maruz kalması konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "pm25_ilerleme",
              soru: "Nüfusun PM2.5'e maruz kalması konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        },
        {
          baslik: "Hava Kirliliği ve Sağlık",
          icon: "pi-cloud",
          aciklama: "Hava kirliliği ve sağlık etkileri",
          sorular: [
            {
              key: "hava_hazirlik",
              soru: "Hava kirliliği, sağlık riskleri ve maliyetleri konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "hava_algı",
              soru: "Hava kirliliği, sağlık riskleri ve maliyetleri konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "hava_yetkinlik",
              soru: "Hava kirliliği, sağlık riskleri ve maliyetleri konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "hava_ilerleme",
              soru: "Hava kirliliği, sağlık riskleri ve maliyetleri konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        },
        {
          baslik: "Su Temini ve Sanitasyon",
          icon: "pi-droplet",
          aciklama: "Su temini ve sanitasyon hizmetleri",
          sorular: [
            {
              key: "su_temini_hazirlik",
              soru: "Su temini, sanitasyon ve kanalizasyon arıtımına erişim konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "su_temini_algı",
              soru: "Su temini, sanitasyon ve kanalizasyon arıtımına erişim konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "su_temini_yetkinlik",
              soru: "Su temini, sanitasyon ve kanalizasyon arıtımına erişim konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "su_temini_ilerleme",
              soru: "Su temini, sanitasyon ve kanalizasyon arıtımına erişim konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        },
        {
          baslik: "Ozon Maruz Kalımı",
          icon: "pi-sun",
          aciklama: "Ozon kirliliği ve maruziyet ölçümleri",
          sorular: [
            {
              key: "ozon_hazirlik",
              soru: "Ozon maruz kalımı konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "ozon_algı",
              soru: "Ozon maruz kalımı konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "ozon_yetkinlik",
              soru: "Ozon maruz kalımı konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "ozon_ilerleme",
              soru: "Ozon maruz kalımı konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        }
      ]
    },
    {
      alan: "Ekonomik Fırsatlar ve Politika Yanıtları",
      icon: "pi-dollar",
      color: "purple",
      aciklama: "Çevresel politika ve ekonomik fırsatların değerlendirilmesi",
      altGruplar: [
        {
          baslik: "Çevreyle İlgili Ürünler",
          icon: "pi-shopping-cart",
          aciklama: "Çevre dostu ürünler ve hizmetler pazarı",
          sorular: [
            {
              key: "cevre_urunleri_hazirlik",
              soru: "Çevreyle ilgili ürünler için pazarlar konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "cevre_urunleri_algı",
              soru: "Çevreyle ilgili ürünler için pazarlar konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "cevre_urunleri_yetkinlik",
              soru: "Çevreyle ilgili ürünler için pazarlar konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "cevre_urunleri_ilerleme",
              soru: "Çevreyle ilgili ürünler için pazarlar konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        },
        {
          baslik: "Çevreyle İlgili Sektörde İstihdam",
          icon: "pi-users",
          aciklama: "Çevre sektöründe istihdam ve katma değer",
          sorular: [
            {
              key: "istihdam_hazirlik",
              soru: "Çevreyle ilgili sektörde istihdam ve katma değer konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "istihdam_algı",
              soru: "Çevreyle ilgili sektörde istihdam ve katma değer konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "istihdam_yetkinlik",
              soru: "Çevreyle ilgili sektörde istihdam ve katma değer konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "istihdam_ilerleme",
              soru: "Çevreyle ilgili sektörde istihdam ve katma değer konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        },
        {
          baslik: "Uluslararası Finansal Akışlar",
          icon: "pi-globe",
          aciklama: "Uluslararası çevresel finansman akışları",
          sorular: [
            {
              key: "finansal_hazirlik",
              soru: "Uluslararası finansal akışlar konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "finansal_algı",
              soru: "Uluslararası finansal akışlar konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "finansal_yetkinlik",
              soru: "Uluslararası finansal akışlar konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "finansal_ilerleme",
              soru: "Uluslararası finansal akışlar konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        },
        {
          baslik: "Vergi ve Sübvansiyonlar",
          icon: "pi-credit-card",
          aciklama: "Çevresel vergiler ve teşvikler",
          sorular: [
            {
              key: "vergiler_hazirlik",
              soru: "Vergiler ve sübvansiyonlar konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "vergiler_algı",
              soru: "Vergiler ve sübvansiyonlar konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "vergiler_yetkinlik",
              soru: "Vergiler ve sübvansiyonlar konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "vergiler_ilerleme",
              soru: "Vergiler ve sübvansiyonlar konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        },
        {
          baslik: "Çevreyle İlgili Teknoloji ve İnovasyon",
          icon: "pi-cog",
          aciklama: "Çevresel teknoloji ve inovasyon uygulamaları",
          sorular: [
            {
              key: "teknoloji_hazirlik",
              soru: "Çevreyle ilgili teknoloji ve inovasyon konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "teknoloji_algı",
              soru: "Çevreyle ilgili teknoloji ve inovasyon konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "teknoloji_yetkinlik",
              soru: "Çevreyle ilgili teknoloji ve inovasyon konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "teknoloji_ilerleme",
              soru: "Çevreyle ilgili teknoloji ve inovasyon konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        },
        {
          baslik: "Yönetmelikler ve Yönetim Yaklaşımları",
          icon: "pi-file-edit",
          aciklama: "Çevresel yönetmelikler ve yönetim sistemleri",
          sorular: [
            {
              key: "yonetmelik_hazirlik",
              soru: "Yönetmelikler ve yönetim yaklaşımları konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "yonetmelik_algı",
              soru: "Yönetmelikler ve yönetim yaklaşımları konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "yonetmelik_yetkinlik",
              soru: "Yönetmelikler ve yönetim yaklaşımları konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "yonetmelik_ilerleme",
              soru: "Yönetmelikler ve yönetim yaklaşımları konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        },
        {
          baslik: "Eğitim ve Beceri Geliştirme",
          icon: "pi-graduation-cap",
          aciklama: "Çevresel eğitim ve kapasite geliştirme",
          sorular: [
            {
              key: "cevre_egitim_hazirlik",
              soru: "Eğitim ve beceri geliştirme konusuna ilişkin hazırlık düzeyimiz yeterlidir.",
              boyut: "Hazırlık"
            },
            {
              key: "cevre_egitim_algı",
              soru: "Eğitim ve beceri geliştirme konusuna ilişkin algı düzeyimiz yeterlidir.",
              boyut: "Algı"
            },
            {
              key: "cevre_egitim_yetkinlik",
              soru: "Eğitim ve beceri geliştirme konusuna ilişkin yetkinlik düzeyimiz yeterlidir.",
              boyut: "Yetkinlik"
            },
            {
              key: "cevre_egitim_ilerleme",
              soru: "Eğitim ve beceri geliştirme konusuna ilişkin ilerleme düzeyimiz yeterlidir.",
              boyut: "İlerleme"
            }
          ]
        }
      ]
    }
  ];

  // Tüm alt grupları düzleştir
  const allAltGruplar = anketSorulari.flatMap(alan => 
    alan.altGruplar.map(altGrup => ({
      ...altGrup,
      alan: alan.alan,
      alanColor: alan.color,
      alanIcon: alan.icon
    }))
  );

  // Sayfa hesaplamaları
  const totalPages = allAltGruplar.length; // Her sayfada 1 alt grup
  const currentAltGrup = allAltGruplar[currentPage];

  // Mevcut sayfadaki soruların tamamlanıp tamamlanmadığını kontrol et
  const isCurrentPageComplete = currentAltGrup ? 
    currentAltGrup.sorular.every(soru => 
      formik.values[soru.key as keyof CevreselGostergelerAnketRequestDto] !== undefined
    ) : false;

  const renderRadioButtons = (fieldName: string) => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {[1, 2, 3, 4, 5].map((value) => (
          <div
            key={value}
            className="flex flex-column align-items-center p-3 border-1 surface-border border-round cursor-pointer hover:surface-100 transition-colors transition-duration-150"
            onClick={() => formik.setFieldValue(fieldName, value)}
          >
            <RadioButton
              inputId={`${fieldName}_${value}`}
              name={fieldName}
              value={value}
              onChange={(e) => formik.setFieldValue(fieldName, e.value)}
              checked={formik.values[fieldName as keyof CevreselGostergelerAnketRequestDto] === value}
            />
            <label htmlFor={`${fieldName}_${value}`} className="text-center text-base mt-2 cursor-pointer">
              <div className="text-sm text-600">
                {value === 1
                  ? "Kesinlikle katılmıyorum"
                  : value === 2
                    ? "Katılmıyorum"
                    : value === 3
                      ? "Kararsızım"
                      : value === 4
                        ? "Katılıyorum"
                        : "Kesinlikle katılıyorum"}
              </div>
            </label>
          </div>
        ))}
      </div>
    );
  };

  const renderMobileRadioButtons = (fieldName: string) => {
    return (
      <div className="flex flex-column gap-2">
        {[1, 2, 3, 4, 5].map((value) => (
          <div
            key={value}
            className="flex align-items-center p-3 border-1 surface-border border-round cursor-pointer hover:surface-100 transition-colors transition-duration-150"
            onClick={() => formik.setFieldValue(fieldName, value)}
          >
            <RadioButton
              inputId={`${fieldName}_${value}`}
              name={fieldName}
              value={value}
              onChange={(e) => formik.setFieldValue(fieldName, e.value)}
              checked={formik.values[fieldName as keyof CevreselGostergelerAnketRequestDto] === value}
            />
            <label htmlFor={`${fieldName}_${value}`} className="flex align-items-center ml-3 cursor-pointer flex-1">
              <span className="text-900 text-base">
                {value === 1
                  ? "Kesinlikle katılmıyorum"
                  : value === 2
                    ? "Katılmıyorum"
                    : value === 3
                      ? "Kararsızım"
                      : value === 4
                        ? "Katılıyorum"
                        : "Kesinlikle katılıyorum"}
              </span>
            </label>
          </div>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="w-full surface-card text-center p-6">
          <i className="pi pi-spin pi-spinner text-4xl text-primary mb-3"></i>
          <p className="text-600">Anket durumu kontrol ediliyor...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="card p-2 md:p-4 p-1 md:p-4">
        <div className="w-full surface-card">
          {/* Header */}
          <div className="text-center mb-2 md:mb-6 p-3 md:p-4 bg-gradient-to-r from-primary-50 to-primary-100 border-round">
            <div className="text-900 text-xl md:text-2xl lg:text-4xl font-bold mb-2 md:mb-3">
              Çevresel Göstergeler Anketi
            </div>
            <p className="text-600 text-xs md:text-sm lg:text-lg leading-relaxed px-2 md:px-0">
              OSB'nizin çevresel göstergeler alanındaki durumunu değerlendirmek için aşağıdaki soruları yanıtlayınız.
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-4 p-3 bg-white border-round shadow-1">
            <div className="flex flex-column md:flex-row align-items-start md:align-items-center justify-content-between mb-2 gap-2">
              <span className="text-900 font-semibold text-sm">Anket İlerlemesi</span>
              <span className="text-600 text-xs">
                {hasSubmitted ? (
                  <span className="text-yellow-600 font-medium flex align-items-center">
                    <i className="pi pi-check-circle mr-1"></i>
                    <span className="whitespace-nowrap">Daha önce doldurdunuz</span>
                  </span>
                ) : (
                  <span className="whitespace-nowrap">
                    {answeredQuestions}/{totalQuestions} soru yanıtlandı
                  </span>
                )}
              </span>
            </div>
            {hasSubmitted ? (
              <div className="bg-green-50 border-1 border-green-200 border-round p-3">
                <div className="flex flex-column md:flex-row align-items-start md:align-items-center justify-content-between gap-3">
                  <div className="flex align-items-center flex-1">
                    <i className="pi pi-check-circle text-green-600 mr-2 text-lg"></i>
                    <span className="text-green-800 font-medium text-sm md:text-base leading-relaxed">
                      Bu anketi daha önce doldurdunuz. İsterseniz yeni bir anket doldurabilirsiniz.
                    </span>
                  </div>
                  <Button
                    label="Yeni Anket Doldur"
                    icon="pi pi-refresh"
                    className="p-button-sm p-button-outlined p-button-success whitespace-nowrap"
                    onClick={() => {
                      formik.resetForm();
                      setHasSubmitted(false);
                    }}
                  />
                </div>
              </div>
            ) : (
              <ProgressBar value={progressPercentage} className="h-2rem" />
            )}
          </div>

          {/* Anket Formu - Sadece doldurulmamışsa göster */}
          {!hasSubmitted && (
            <>
              {/* Giriş Yapma Uyarısı - Kullanıcı giriş yapmamışsa sadece bu gösterilir */}
              {!auth.getUser().access_token ? (
                <div className="bg-orange-50 border-1 border-orange-200 border-round p-4 text-center">
                  <div className="flex flex-column align-items-center">
                    <i className="pi pi-exclamation-triangle text-orange-600 text-4xl mb-3"></i>
                    <h3 className="text-orange-800 font-bold text-xl mb-2">Anket Doldurmak İçin Giriş Yapın</h3>
                    <p className="text-orange-700 text-base mb-4">
                      Bu anketi doldurmak için hesabınıza giriş yapmanız gerekmektedir.
                    </p>
                    <Button
                      label="Giriş Yap"
                      icon="pi pi-sign-in"
                      className="p-button-lg p-button-orange"
                      onClick={() => navigate("/loginsebis", { state: { from: window.location.pathname } })}
                    />
                  </div>
                </div>
              ) : (
                <form onSubmit={formik.handleSubmit}>
                  {/* Sayfa Navigasyonu */}
                  <div className="flex justify-content-between align-items-center mb-4 p-3 bg-white border-round shadow-1">
                <div className="flex align-items-center gap-2">
                  <span className="text-600 text-sm">Sayfa:</span>
                  <span className="font-bold text-900">{currentPage + 1} / {totalPages}</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    icon="pi pi-chevron-left"
                    className="p-button-sm p-button-outlined"
                    disabled={currentPage === 0}
                    onClick={() => setCurrentPage(currentPage - 1)}
                    tooltip="Önceki Sayfa"
                  />
                </div>
              </div>

              {/* Mevcut Sayfa Alt Grubu */}
              {currentAltGrup && (
                <Card className="mb-3 shadow-1 border-round">
                  {/* Ana Başlık */}
                  <div className="flex align-items-center mb-4 p-4 bg-gradient-to-r from-primary-50 to-primary-100 border-round">
                    <div className={`p-3 border-round mr-4 bg-${currentAltGrup.alanColor}-50`}>
                      <i className={`pi ${currentAltGrup.alanIcon} text-2xl text-${currentAltGrup.alanColor}-600`}></i>
                    </div>
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-900 mb-2">{currentAltGrup.alan}</h2>
                      <p className="text-600 text-base">Ana Kategori</p>
                    </div>
                    <div className="text-right">
                      <span className="text-base text-500">
                        Sayfa {currentPage + 1} / {totalPages}
                      </span>
                    </div>
                  </div>

                  {/* Alt Başlık */}
                  <div className="flex align-items-center mb-4 p-4 bg-white border-1 surface-border border-round">
                    <div className={`p-3 border-round mr-4 bg-${currentAltGrup.alanColor}-100`}>
                      <i className={`pi ${currentAltGrup.icon} text-xl text-${currentAltGrup.alanColor}-600`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-900 mb-2">{currentAltGrup.baslik}</h3>
                      {currentAltGrup.aciklama && (
                        <p className="text-600 text-base">{currentAltGrup.aciklama}</p>
                      )}
                    </div>
                    <div className="text-right">
                      {isCurrentPageComplete ? (
                        <div className="flex align-items-center text-green-600">
                          <i className="pi pi-check-circle mr-2 text-lg"></i>
                          <span className="text-base font-medium">Tamamlandı</span>
                        </div>
                      ) : (
                        <div className="flex align-items-center text-orange-600">
                          <i className="pi pi-exclamation-triangle mr-2 text-lg"></i>
                          <span className="text-base font-medium">4 soruyu yanıtlayın</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 4 Soru Tipi */}
                  <div className="grid grid-cols-1 gap-2">
                    {currentAltGrup.sorular.map((soru, soruIndex) => (
                      <div
                        key={soruIndex}
                        className={`p-4 border-1 surface-border border-round bg-white hover:shadow-1 transition-all transition-duration-200 ${
                          formik.values[soru.key as keyof CevreselGostergelerAnketRequestDto] === undefined 
                            ? 'border-orange-300 bg-orange-50' 
                            : 'border-green-300 bg-green-50'
                        }`}
                      >
                        <div className="flex align-items-center mb-3">
                          <span
                            className={`text-base font-bold text-white px-3 py-1 border-round-full bg-${currentAltGrup.alanColor}-500`}
                          >
                            {soru.boyut}
                          </span>
                          <span className="text-600 text-sm ml-2">Soru {soruIndex + 1}/4</span>
                          {formik.values[soru.key as keyof CevreselGostergelerAnketRequestDto] !== undefined && (
                            <i className="pi pi-check-circle text-green-600 ml-auto text-lg"></i>
                          )}
                        </div>
                        <p className="text-900 font-medium mb-2 text-lg leading-relaxed">{soru.soru}</p>
                        {/* Desktop Radio Buttons */}
                        <div className="hidden md:block">{renderRadioButtons(soru.key)}</div>
                        {/* Mobile Radio Buttons */}
                        <div className="block md:hidden">{renderMobileRadioButtons(soru.key)}</div>
                        {formik.errors[soru.key as keyof CevreselGostergelerAnketRequestDto] && (
                          <small className="p-error block mt-2">
                            {formik.errors[soru.key as keyof CevreselGostergelerAnketRequestDto]}
                          </small>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Alt Navigasyon Butonları */}
              <div className="flex justify-content-between align-items-center mt-4 mb-3">
                
                <div className="flex gap-2">
                  {currentPage === totalPages - 1 ? (
                    <Button
                      label="Anketi Gönder"
                      type="submit"
                      className="p-button-success px-4 py-2 text-base"
                      disabled={formik.isSubmitting || !isCurrentPageComplete}
                      icon="pi pi-send"
                      iconPos="right"
                    />
                  ) : (
                    <Button
                      label="İleri"
                      icon="pi pi-chevron-right"
                      iconPos="right"
                      className="p-button"
                      disabled={!isCurrentPageComplete}
                      onClick={() => {
                        setCurrentPage(currentPage + 1);
                        // Sayfanın en üstüne yönlendir
                        setTimeout(() => {
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                          // Alternatif yöntem
                          document.documentElement.scrollTop = 0;
                          document.body.scrollTop = 0;
                        }, 100);
                      }}
                    />
                  )}
                </div>
              </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
      <ProtokolFirmalarComponent />
      {/* Onay Dialog'u */}
      <Dialog
        visible={showConfirmDialog}
        onHide={() => setShowConfirmDialog(false)}
        header="Anket Güncelleme Onayı"
        className="w-90 md:w-50"
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="İptal"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setShowConfirmDialog(false)}
            />
            <Button
              label="Güncelle"
              icon="pi pi-check"
              className="p-button-success"
              onClick={() => submitSurvey(formik.values)}
            />
          </div>
        }
      >
        <div className="flex align-items-center mb-3">
          <i className="pi pi-exclamation-triangle text-yellow-600 text-2xl mr-3"></i>
          <div>
            <h3 className="text-lg font-bold mb-2">Dikkat!</h3>
            <p className="text-600 mb-3">
              Bu anketi daha önce gönderdiniz. Mevcut cevaplarınızı güncellemek istediğinizden emin misiniz?
            </p>
            <p className="text-sm text-500">Not: Yeni gönderim, önceki cevaplarınızın üzerine yazılacaktır.</p>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default CevreselGostergelerAnket;
